import { A, Navigate, RouteSectionProps, useNavigate } from "@solidjs/router";
import { Reaction } from "applesauce-common/casts";
import { ReactionFactory } from "applesauce-common/factories";
import { ReactionsModel } from "applesauce-common/models";
import {
  castEventStream,
  castTimelineStream,
} from "applesauce-common/observable";
import { DeleteFactory } from "applesauce-core/factories";
import { nip19 } from "nostr-tools";
import { createEffect, createMemo, createSignal, from } from "solid-js";

import { WifiNetwork } from "../../casts/wifi-network";
import CopyButton from "../../components/copy-button";
import {
  BackIcon,
  DislikeIcon,
  LikeIcon,
  QrCodeIcon,
} from "../../components/icons";
import UserAvatar from "../../components/user-avatar";
import UserFollowButton from "../../components/user-follow-button";
import UserLink from "../../components/user-link";
import { asyncAction } from "../../helpers/async-action";
import { createWifiQrCode } from "../../helpers/qr-code";
import { accounts } from "../../services/accounts";
import { publish } from "../../services/pool";
import { eventStore } from "../../services/stores";
import WifiComments from "./components/wifi-comments";
import WifiQrModal from "./components/wifi-qr-modal";

function WifiPage(props: { wifi: WifiNetwork }) {
  const account = from(accounts.active$);
  const name = props.wifi.name;
  const ssid = props.wifi.ssid;
  const password = props.wifi.password;
  const hidden = props.wifi.hidden;

  const link = createWifiQrCode({
    ssid,
    password,
    hidden,
  });

  const reactions = from(
    eventStore
      .model(ReactionsModel, props.wifi.event)
      .pipe(castTimelineStream(Reaction, eventStore)),
  );
  const likes = createMemo(() => reactions()?.filter((e) => e.content === "+"));
  const dislikes = createMemo(() =>
    reactions()?.filter((e) => e.content === "-"),
  );

  const hasLiked = createMemo(() =>
    likes()?.some((e) => e.event.pubkey === account()?.pubkey),
  );
  const hasDisliked = createMemo(() =>
    dislikes()?.some((e) => e.event.pubkey === account()?.pubkey),
  );

  const removeExistingReactions = async () => {
    const existing = reactions()
      ?.filter((e) => e.event.pubkey === account()?.pubkey)
      .map((e) => e.event);

    if (existing && existing.length > 0) {
      const signed = await DeleteFactory.fromEvents(
        existing,
        "Removing duplicate reactions",
      ).sign(accounts.signer);
      await publish(signed);
    }
  };

  const like = asyncAction(async () => {
    await removeExistingReactions();
    const signed = await ReactionFactory.like(props.wifi.event).sign(
      accounts.signer,
    );
    await publish(signed);
  });

  const dislike = asyncAction(async () => {
    await removeExistingReactions();
    const signed = await ReactionFactory.dislike(props.wifi.event).sign(
      accounts.signer,
    );
    await publish(signed);
  });

  return (
    <>
      <div class="card bg-base-100 shadow mb-6">
        <div class="card-body p-4">
          <h1 class="card-title text-primary">{name || ssid}</h1>

          <div class="flex gap-2 items-center">
            <UserAvatar pubkey={props.wifi.event.pubkey} />
            <UserLink class="text-lg" pubkey={props.wifi.event.pubkey} />

            <UserFollowButton
              pubkey={props.wifi.event.pubkey}
              class="btn btn-primary btn-sm ml-auto rounded-full"
            />
          </div>

          <div class="mt-3 flex flex-col gap-2">
            <div class="flex gap-2 items-center justify-between">
              <div class="font-semibold">SSID:</div>
              <div class="flex items-center gap-2">
                <code class="kbd select-all min-w-32">{ssid}</code>
                {ssid && <CopyButton text={ssid} />}
              </div>
            </div>

            {password && (
              <div class="flex gap-2 items-center justify-between">
                <div class="font-semibold">Password:</div>
                <div class="flex items-center gap-2">
                  <code class="kbd select-all min-w-32">{password}</code>
                  <CopyButton text={password} />
                </div>
              </div>
            )}
          </div>
          <p class="mt-4 text-base-content/70">{props.wifi.description}</p>

          <div class="mt-5 flex items-center gap-4">
            <button
              onClick={like.run}
              class={`btn btn-sm rounded-full ${hasLiked() ? "btn-primary" : "btn-ghost"}`}
              disabled={like.loading() || !account()}
            >
              <LikeIcon />
              <span>{likes()?.length ?? 0}</span>
            </button>

            <button
              onClick={dislike.run}
              class={`btn btn-sm rounded-full ${hasDisliked() ? "btn-error" : "btn-ghost"}`}
              disabled={dislike.loading() || !account()}
            >
              <DislikeIcon />
              <span>{dislikes()?.length ?? 0}</span>
            </button>

            <a href={link} class="btn btn-primary ms-auto rounded-full">
              Connect
            </a>
          </div>
        </div>
      </div>

      <WifiComments wifi={props.wifi.event} />
    </>
  );
}

export default function WifiView(props: RouteSectionProps) {
  const { naddr } = props.params;
  if (!naddr) return <Navigate href="/" />;

  const decoded = nip19.decode(naddr);
  if (decoded.type !== "naddr") return <Navigate href="/" />;

  const pointer = decoded.data;

  const account = from(accounts.active$);
  const navigate = useNavigate();
  const wifi = from(
    eventStore
      .addressable(pointer)
      .pipe(castEventStream(WifiNetwork, eventStore)),
  );
  const [showQrModal, setShowQrModal] = createSignal(false);

  // debug wifi event
  createEffect(() => console.log(wifi()));

  return (
    <>
      <main class="flex-col flex-grow flex overflow-auto bg-base-200 p-4">
        {wifi() ? (
          <WifiPage wifi={wifi()!} />
        ) : (
          <span class="loading loading-spinner loading-lg self-center"></span>
        )}
      </main>

      <footer class="bg-primary text-primary-content p-2 pb-safe-or-2 flex items-center gap-2">
        <button class="btn btn-ghost btn-square" onClick={() => navigate(-1)}>
          <BackIcon />
        </button>

        <div class="flex-grow"></div>

        {wifi() && account()?.pubkey === wifi()?.event.pubkey && (
          <A
            href={`/wifi/${naddr}/edit`}
            class="btn btn-ghost"
            aria-label="edit"
          >
            Edit
          </A>
        )}
        <button
          class="btn btn-ghost btn-square"
          aria-label="show qr code"
          onClick={() => setShowQrModal(true)}
        >
          <QrCodeIcon />
        </button>
      </footer>

      {/* QR Code Modal */}
      {showQrModal() && wifi() && (
        <WifiQrModal wifi={wifi()!} onClose={() => setShowQrModal(false)} />
      )}
    </>
  );
}
